import moment from 'moment'
import { useState } from 'react'
import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { TalentRankedDeveloper } from '../../services/EmployeeService'
import { EMPLOYEE_HIGHLIGHT_ICON_MAP, EMPLOYEE_HIGHLIGHT_ROCKSTAR } from '../../constants/employee'
import { SortOrderEnum } from '../../types/enums/sort-order.enum'

interface TopTalentTableProps {
  developers: TalentRankedDeveloper[]
  loading?: boolean
  onDeveloperClick: (developer: TalentRankedDeveloper) => void
}

const formatLeaders = (leaders: TalentRankedDeveloper['leaders']): string => {
  if (!leaders?.length) {
    return '—'
  }
  return leaders.map(leader => leader.name).join(', ')
}

const formatLastReviewDate = (lastReviewDate: string): string => {
  if (!lastReviewDate) {
    return '—'
  }
  return moment(lastReviewDate).format('ll')
}

const getHighlightIcon = (selectedTalentRank: string | undefined) => {
  if (!selectedTalentRank) {
    return undefined
  }
  return selectedTalentRank.toLowerCase() === EMPLOYEE_HIGHLIGHT_ROCKSTAR.toLowerCase()
    ? EMPLOYEE_HIGHLIGHT_ICON_MAP[EMPLOYEE_HIGHLIGHT_ROCKSTAR]
    : undefined
}

export const TopTalentTable = ({ developers, loading = false, onDeveloperClick }: TopTalentTableProps): JSX.Element => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalContent, setModalContent] = useState('')

  const handleInfoClick = (reason: string) => (e: React.MouseEvent) => {
    e.stopPropagation()
    setModalContent(reason)
    setIsModalOpen(true)
  }

  return (
    <>
      <DataTable
        className="app-table-container"
        tableClassName="app-table team-table top-talent-view"
        rowClassName={() => ({
          'user-list-table-row --done': true,
        })}
        value={developers}
        dataKey="id"
        paginator
        rows={10}
        loading={loading}
        rowsPerPageOptions={[5, 10, 25, 50]}
        emptyMessage="No talent information available yet."
        onRowClick={(e: any) => onDeveloperClick(e.data)}
      >
        <Column
          headerClassName="user-list-table-header w-[18rem]"
          bodyClassName="align-middle"
          field="name"
          header="Developer"
          sortable
          body={(rowData: TalentRankedDeveloper) => {
            const highlightIcon = getHighlightIcon(rowData.selectedTalentRank)

            return (
              <div className="flex flex-col">
                <button
                  type="button"
                  onClick={() => onDeveloperClick(rowData)}
                  className="text-left focus:outline-none no-underline hover:no-underline focus:no-underline"
                >
                  <span className="inline-flex items-center text-xl font-semibold text-orange cursor-pointer clickable-hover-effect clickable-hover-effect--blue no-underline hover:no-underline focus:no-underline">
                    {highlightIcon && <span className="mr-2">{highlightIcon}</span>}
                    <span>{rowData.name}</span>
                  </span>
                </button>
              </div>
            )
          }}
        />
        <Column
          headerClassName="user-list-table-header"
          bodyClassName="align-middle"
          field="jobTitle"
          header="Job Title"
          sortable
          body={(rowData: TalentRankedDeveloper) => rowData.jobTitle || '—'}
        />
        <Column
          headerClassName="user-list-table-header w-[10rem]"
          bodyClassName="align-middle"
          field="suggestedTalentRank"
          header="Suggested Category"
          sortable
          body={(rowData: TalentRankedDeveloper) => rowData.suggestedTalentRank || '—'}
        />
        <Column
          headerClassName="user-list-table-header"
          bodyClassName="align-middle"
          field="selectedTalentRank"
          header="Selected Category"
          sortable
          body={(rowData: TalentRankedDeveloper) => {
            const showInfoIcon =
              rowData.selectedTalentRank &&
              rowData.suggestedTalentRank &&
              rowData.selectedTalentRank !== rowData.suggestedTalentRank &&
              rowData.overrideReason

            return (
              <div className="flex items-center gap-2">
                <span>{rowData.selectedTalentRank || '—'}</span>
                {showInfoIcon && (
                  <button
                    onClick={handleInfoClick(rowData.overrideReason!)}
                    className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-orange text-white text-xs font-bold cursor-pointer hover:bg-dark-blue transition-colors"
                    title="View override justification"
                  >
                    i
                  </button>
                )}
              </div>
            )
          }}
        />
        <Column
          headerClassName="user-list-table-header"
          bodyClassName="align-middle"
          field="leaders"
          header="Leader(s)"
          body={(rowData: TalentRankedDeveloper) => formatLeaders(rowData.leaders)}
        />
        <Column
          headerClassName="user-list-table-header w-[10rem]"
          bodyClassName="align-middle"
          field="lastReviewDate"
          header="Last Review"
          sortable
          body={(rowData: TalentRankedDeveloper) => formatLastReviewDate(rowData.lastReviewDate)}
        />
      </DataTable>
      {isModalOpen && modalContent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm p-6 sm:p-10">
          <div className="bg-white w-full max-w-3xl rounded-3xl border border-orange/10 shadow-[0_24px_80px_rgba(15,23,42,0.18)] overflow-hidden">
            <header className="flex items-center justify-between gap-4 bg-gradient-to-r from-pale-cyan to-white px-8 sm:px-12 py-6 sm:py-8">
              <span className="text-2xl font-semibold text-dark-blue">Override Justification</span>
              <button
                className="inline-flex items-center rounded-full border border-orange/20 px-4 py-2 text-sm font-medium text-orange hover:bg-pale-cyan hover:text-dark-blue transition-colors"
                onClick={() => setIsModalOpen(false)}
              >
                Close
              </button>
            </header>
            <section className="px-8 sm:px-12 py-8 sm:py-12 bg-white">
              <div className="max-h-[60vh] overflow-y-auto pr-1 text-dark-blue text-lg leading-relaxed whitespace-pre-wrap">
                {modalContent}
              </div>
            </section>
          </div>
        </div>
      )}
    </>
  )
}
